import React from 'react';
import {
  CustomProvider,
  Dropdown,
  Nav,
  Popover,
  Sidenav,
  Tooltip,
  Whisper,
} from 'rsuite';
import ExitIcon from '@rsuite/icons/Exit';
import EmailIcon from '@rsuite/icons/Email';
import PageIcon from '@rsuite/icons/Page';
import ImageIcon from '@rsuite/icons/Image';
import MemberIcon from '@rsuite/icons/Member';
import ArrowLeftLineIcon from '@rsuite/icons/ArrowLeftLine';
import ArrowRightLineIcon from '@rsuite/icons/ArrowRightLine';
import ChangeListIcon from '@rsuite/icons/ChangeList';
import GridIcon from '@rsuite/icons/Grid';
import TagIcon from '@rsuite/icons/Tag';
import PeopleFolderIcon from '@rsuite/icons/PeopleFolder';
import PeopleSpeakerIcon from '@rsuite/icons/PeopleSpeaker';
import PeoplesMapIcon from '@rsuite/icons/PeoplesMap';
import AdminIcon from '@rsuite/icons/Admin';
import type { IconProps } from '@rsuite/icons/Icon';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import Partners from '../pages/partners/Partners';
import Requests from '../pages/requests/Requests';
import ContactRequests from '../pages/contactRequests/ContactRequests';
import Users from '../pages/users/Users';
import Courses from '../pages/courses/Courses';
import * as auth from '../api/auth';
import { useAuth } from '../common/AuthContext';
import ThemeToggleIcon from '../common/ThemeToggleIcon';
import Students from '../pages/students/Students';
import Groups from '../pages/groups/Groups';
import Trainers from '../pages/trainers/Trainers';
import Gallery from '../pages/gallery/Gallery';
import BlogPosts from '../pages/blog/BlogPosts';
import BlogCategories from '../pages/blogCategories/BlogCategories';
import Dashboard from '../pages/dashboard/Dashboard';
import DashboardIcon from '@rsuite/icons/Dashboard';

const SIDEBAR_EXPANDED_KEY = 'sidebar_expanded';
const THEME_KEY = 'admin_theme';

export default function Navigator() {
  const navigate = useNavigate();
  const location = useLocation();

  const [expanded, setExpanded] = React.useState(() => {
    const saved = localStorage.getItem(SIDEBAR_EXPANDED_KEY);
    return saved === 'true';
  });

  const [theme, setTheme] = React.useState<'light' | 'dark'>(() => {
    return (localStorage.getItem(THEME_KEY) as 'light' | 'dark') || 'light';
  });

  const { user, markGuest } = useAuth();
  const userEmail = user?.email ?? '';

  React.useEffect(() => {
    localStorage.setItem(SIDEBAR_EXPANDED_KEY, String(expanded));
  }, [expanded]);

  React.useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const toggleTheme = () => setTheme(t => (t === 'light' ? 'dark' : 'light'));

  const avatarLetter = userEmail ? userEmail[0].toUpperCase() : '?';

  const handleLogout = () => {
    auth
      .logout()
      .then(() => {
        markGuest();
        navigate('/login');
      })
      .catch((err: any) => {
        console.error(err);
        // On 401 the API client already redirects via window.location.
        if (err?.status !== 401) {
          markGuest();
          navigate('/login');
        }
      });
  };

  const NavItemWithTooltip = ({
    eventKey,
    icon,
    children,
    onClick,
    disabled = false,
  }: {
    eventKey: string;
    icon: React.ReactElement<IconProps>;
    children: React.ReactNode;
    onClick: () => void;
    disabled?: boolean;
  }) => {
    const navItem = (
      <Nav.Item
        eventKey={eventKey}
        onClick={disabled ? undefined : onClick}
        icon={icon}
        disabled={disabled}
        className={disabled ? 'nav-item-disabled' : undefined}
      >
        {children}
      </Nav.Item>
    );

    if (expanded) {
      return navItem;
    }

    return (
      <Whisper
        placement="right"
        trigger="hover"
        speaker={
          <Tooltip>
            {children}
            {disabled ? ' (тимчасово вимкнено)' : ''}
          </Tooltip>
        }
      >
        {navItem}
      </Whisper>
    );
  };

  const activeKey = React.useMemo(() => {
    if (location.pathname.startsWith('/dashboard')) {
      return 'dashboard';
    }
    if (location.pathname.startsWith('/courses')) {
      return 'courses';
    }
    if (location.pathname.startsWith('/trainers')) {
      return 'trainers';
    }
    if (location.pathname.startsWith('/groups')) {
      return 'groups';
    }
    if (location.pathname.startsWith('/students')) {
      return 'students';
    }
    if (location.pathname.startsWith('/partners')) {
      return 'partners';
    }
    if (location.pathname.startsWith('/gallery')) {
      return 'gallery';
    }
    if (location.pathname.startsWith('/blog-categories')) {
      return 'blog-categories';
    }
    if (location.pathname.startsWith('/blog')) {
      return 'blog';
    }
    if (location.pathname.startsWith('/users')) {
      return 'users';
    }
    if (location.pathname.startsWith('/contact-requests')) {
      return 'contact-requests';
    }
    return 'requests';
  }, [location.pathname]);

  const sidebarWidth = expanded ? 220 : 56;

  return (
    <CustomProvider theme={theme}>
      <div
        className={`app-shell ${expanded ? 'is-expanded' : ''}`}
        style={{ width: '100%' }}
      >
        <Sidenav
          expanded={expanded}
          className="app-sidenav"
          style={{ width: sidebarWidth }}
        >
          <div className="sidenav-inner">
            <Sidenav.Header>
              <div className="sidenav-header-content">
                <img
                  src={
                    theme === 'dark'
                      ? '/image/logo/main_white_logo.png'
                      : '/image/logo/main_dark_logo.png'
                  }
                  alt="Крила"
                  className="sidenav-logo"
                />
                <button
                  className="sidenav-toggle"
                  onClick={() => setExpanded(!expanded)}
                  aria-label={expanded ? 'Згорнути меню' : 'Розгорнути меню'}
                >
                  {expanded ? <ArrowLeftLineIcon /> : <ArrowRightLineIcon />}
                </button>
              </div>
            </Sidenav.Header>
            <Sidenav.Body className="sidenav-body">
              <Nav
                appearance="subtle"
                activeKey={activeKey}
                className="nav-main"
                vertical
              >
                <div className="nav-section">
                  <NavItemWithTooltip
                    eventKey="dashboard"
                    onClick={() => navigate('/dashboard')}
                    icon={<DashboardIcon />}
                  >
                    Головна
                  </NavItemWithTooltip>
                </div>

                <div className="nav-section">
                  <div className="nav-section-title">Заявки</div>
                  <NavItemWithTooltip
                    eventKey="requests"
                    onClick={() => navigate('/requests')}
                    icon={<ChangeListIcon />}
                  >
                    Анкети
                  </NavItemWithTooltip>
                  <NavItemWithTooltip
                    eventKey="contact-requests"
                    onClick={() => navigate('/contact-requests')}
                    icon={<EmailIcon />}
                  >
                    Контактні заявки
                  </NavItemWithTooltip>
                </div>

                <div className="nav-section">
                  <div className="nav-section-title">Навчання</div>
                  <NavItemWithTooltip
                    eventKey="courses"
                    onClick={() => navigate('/courses')}
                    icon={<GridIcon />}
                  >
                    Курси
                  </NavItemWithTooltip>
                  <NavItemWithTooltip
                    eventKey="trainers"
                    onClick={() => navigate('/trainers')}
                    icon={<PeopleSpeakerIcon />}
                  >
                    Тренери
                  </NavItemWithTooltip>
                  <NavItemWithTooltip
                    eventKey="groups"
                    onClick={() => navigate('/groups')}
                    icon={<PeoplesMapIcon />}
                  >
                    Групи
                  </NavItemWithTooltip>
                  <NavItemWithTooltip
                    eventKey="students"
                    onClick={() => navigate('/students')}
                    icon={<MemberIcon />}
                  >
                    Студенти
                  </NavItemWithTooltip>
                </div>

                <div className="nav-section">
                  <div className="nav-section-title">Контент</div>
                  <NavItemWithTooltip
                    eventKey="partners"
                    onClick={() => navigate('/partners')}
                    icon={<PeopleFolderIcon />}
                  >
                    Партнери
                  </NavItemWithTooltip>
                  <NavItemWithTooltip
                    eventKey="blog"
                    onClick={() => navigate('/blog')}
                    icon={<PageIcon />}
                  >
                    Блог
                  </NavItemWithTooltip>
                  <NavItemWithTooltip
                    eventKey="blog-categories"
                    onClick={() => navigate('/blog-categories')}
                    icon={<TagIcon />}
                  >
                    Категорії блогу
                  </NavItemWithTooltip>
                  <NavItemWithTooltip
                    eventKey="gallery"
                    onClick={() => navigate('/gallery')}
                    icon={<ImageIcon />}
                    disabled={true}
                  >
                    Галерея
                  </NavItemWithTooltip>
                </div>

                <div className="nav-section">
                  <div className="nav-section-title">Система</div>
                  <NavItemWithTooltip
                    eventKey="users"
                    onClick={() => navigate('/users')}
                    icon={<AdminIcon />}
                  >
                    Адміністратори
                  </NavItemWithTooltip>
                </div>
              </Nav>
            </Sidenav.Body>
            {/* Sticky footer — sits outside the scrollable body so the user
                avatar/email is always visible without scrolling. */}
            <div className="nav-footer">
              <Whisper
                placement="top"
                trigger="click"
                speaker={
                  <Popover className="user-menu-popover">
                    <Dropdown.Menu>
                      <Dropdown.Item
                        onClick={toggleTheme}
                        className="theme-toggle-item"
                      >
                        <span className="theme-toggle-item-content">
                          <ThemeToggleIcon theme={theme} />
                          <span>
                            {theme === 'light' ? 'Темна тема' : 'Світла тема'}
                          </span>
                        </span>
                      </Dropdown.Item>
                      <Dropdown.Item
                        icon={<ExitIcon />}
                        onClick={handleLogout}
                        className="logout-item"
                      >
                        Вихід
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Popover>
                }
              >
                <button className="user-menu-trigger">
                  <span className="user-avatar">{avatarLetter}</span>
                  {expanded && <span className="user-email">{userEmail}</span>}
                </button>
              </Whisper>
            </div>
          </div>
        </Sidenav>

        <div className="page-container">
          <Routes>
            <Route path="/dashboard" Component={Dashboard} />
            <Route path="/requests" Component={Requests} />
            <Route path="/contact-requests" Component={ContactRequests} />
            <Route path="/partners" Component={Partners} />
            <Route path="/users" Component={Users} />
            <Route path="/courses" Component={Courses} />
            <Route path="/students" Component={Students} />
            <Route path="/groups" Component={Groups} />
            <Route path="/trainers" Component={Trainers} />
            <Route path="/gallery" Component={Gallery} />
            <Route path="/blog" Component={BlogPosts} />
            <Route path="/blog-categories" Component={BlogCategories} />
            <Route path="/*" element={<Navigate replace to="/dashboard" />} />
          </Routes>
        </div>
      </div>
    </CustomProvider>
  );
}
